import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { ImagePlus, ChevronDown, ChevronUp, X } from "lucide-react";
import type { ContentLibrary, LibraryEntry, RecordKind } from "../api/contract";
import type { LibraryController } from "../hooks/useLibrary";
import { isValidItemLink } from "../lib/library";
import { Skeleton, SkeletonGroup } from "./Skeleton";
import { Spinner } from "./Spinner";

const KINDS: Record<RecordKind, string> = {
  profile: "About you",
  copy: "Text",
  service: "Service",
  project: "Project",
  faq: "Question",
  link: "Link",
  media: "Photo",
  contact: "Contact",
  social: "Social link",
  item: "Item",
  post: "Post",
};

export const FIELDS: Record<string, string> = {
  name: "Name",
  bio: "Short description",
  title: "Title",
  body: "Text",
  description: "Description",
  question: "Question",
  answer: "Answer",
  label: "Button text",
  url: "Goes to",
  alt: "Photo description",
  whatsapp: "WhatsApp number",
  message: "Pre-filled message",
  phone: "Phone number",
  email: "Email address",
  platform: "Platform",
  text: "Text",
  detail: "Detail",
};

/** Fields short enough for one line. */
const SHORT = new Set(["name", "title", "question", "label", "url", "whatsapp", "phone", "email", "platform", "detail"]);

function where(entry: LibraryEntry): string {
  const kind = KINDS[entry.kind];
  if (entry.usedOn.length > 0) {
    const pages = entry.usedOn.map((page) => page.title);
    return `${kind} · ${pages.length > 2 ? `${pages.slice(0, 2).join(", ")} and ${pages.length - 2} more` : pages.join(", ")}`;
  }
  return entry.shared ? `${kind} · On every page` : kind;
}

/** Records that can carry an optional photo and link. */
const ITEM_KINDS: readonly RecordKind[] = ["service", "project", "item"];

/** The fields the form edits: for items, the link is always there, even while empty. */
function formValues(entry: LibraryEntry): Record<string, string> {
  return ITEM_KINDS.includes(entry.kind) ? { ...entry.fields, url: entry.fields.url ?? "" } : entry.fields;
}

interface ItemPhotoProps {
  photos: LibraryEntry[];
  value: string | null;
  /** The photo already saved on the item, used until the library lists it. */
  saved?: LibraryEntry["image"];
  onChange: (id: string | null) => void;
}

/** Picks an item's photo from the photos already on the site; the owner can also remove it. */
function ItemPhoto({ photos, value, saved, onChange }: ItemPhotoProps) {
  const [choosing, setChoosing] = useState(false);
  const current = photos.find((photo) => photo.id === value);
  const src = current?.src ?? (saved?.id === value ? saved?.src : undefined);
  const description = current?.fields.alt ?? (saved?.id === value ? saved?.alt : undefined);
  return (
    <div className="bw-item-photo">
      <span>Photo</span>
      <div className="bw-item-photo-row">
        {value && src ? <img className="bw-thumb" src={src} alt={description ?? ""} /> : <span className="bw-item-photo-empty">No photo</span>}
        <button type="button" className="bw-btn bw-btn--quiet" onClick={() => setChoosing((now) => !now)} disabled={photos.length === 0 && !value}>
          <ImagePlus size={16} aria-hidden="true" /> {value ? "Change" : "Choose photo"}
        </button>
        {value && <button type="button" className="bw-btn bw-btn--text" onClick={() => { onChange(null); setChoosing(false); }}>Remove</button>}
      </div>
      {photos.length === 0 && !value && <p className="bw-item-note">Add photos in the Photos tab first, then pick one here.</p>}
      {choosing && (
        <ul className="bw-item-photo-grid">
          {photos.map((photo) => (
            <li key={photo.id}>
              <button type="button" className="bw-item-photo-choice" aria-pressed={photo.id === value} aria-label={photo.label} onClick={() => { onChange(photo.id); setChoosing(false); }}>
                {photo.src && <img className="bw-thumb" src={photo.src} alt="" loading="lazy" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface ItemProps {
  entry: LibraryEntry;
  library: LibraryController;
  onPutBack?: (entry: LibraryEntry) => void;
  /** The full, ordered ids of the list `entry` belongs to, so Move up/down can submit the new order. */
  listOrder?: string[];
}

export function Item({ entry, library, onPutBack, listOrder }: ItemProps) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(() => formValues(entry));
  const [image, setImage] = useState<string | null>(entry.image?.id ?? null);
  const hasExtras = ITEM_KINDS.includes(entry.kind);
  const photos = library.library?.entries.filter((candidate) => candidate.kind === "media") ?? [];
  const saving = library.change?.recordId === entry.id && library.change.status === "saving";
  const reordering = library.change?.recordId === entry.id && library.change.status === "saving" && Boolean(entry.list);
  const failed = library.change?.recordId === entry.id && library.change.status === "failed";
  const original = formValues(entry);
  const changed: Record<string, string | null> = Object.fromEntries(Object.entries(values).filter(([field, value]) => value !== original[field]));
  if (hasExtras && image !== (entry.image?.id ?? null)) changed.image = image;
  // An item's link may be left empty; everything else must have text.
  const blank = Object.entries(values).some(([field, value]) => !(hasExtras && field === "url") && !value.trim());
  const badLink = hasExtras && values.url !== undefined && values.url.trim() !== "" && !isValidItemLink(values.url);
  const editable = true;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (Object.keys(changed).length === 0 || blank || badLink) return;
    if (await library.save(entry.id, changed)) setOpen(false);
  };

  const move = (direction: -1 | 1) => {
    if (!entry.list || !listOrder) return;
    const index = listOrder.indexOf(entry.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= listOrder.length) return;
    const next = [...listOrder];
    [next[index], next[target]] = [next[target]!, next[index]!];
    void library.reorder(entry.id, entry.list.collectionId, next);
  };

  return (
    <li className="bw-item" data-open={open || undefined}>
      <div className="bw-item-row">
        {entry.src && <img className="bw-thumb" src={entry.src} alt="" loading="lazy" />}
        {entry.list && listOrder && (
          <div className="bw-item-order">
            <button type="button" className="bw-btn bw-btn--icon" aria-label={`Move ${entry.label} up`} disabled={reordering || entry.list.index === 0} onClick={() => move(-1)}>
              <ChevronUp size={16} aria-hidden="true" />
            </button>
            <button type="button" className="bw-btn bw-btn--icon" aria-label={`Move ${entry.label} down`} disabled={reordering || entry.list.index === entry.list.count - 1} onClick={() => move(1)}>
              <ChevronDown size={16} aria-hidden="true" />
            </button>
          </div>
        )}
        <button
          type="button"
          className="bw-item-head"
          aria-expanded={open}
          onClick={() => {
            setValues(formValues(entry));
            setImage(entry.image?.id ?? null);
            setOpen((now) => !now);
          }}
        >
          <span className="bw-item-label">{entry.label}</span>
          <span className="bw-item-meta">{where(entry)}</span>
        </button>
        {open && <button type="button" className="bw-btn bw-btn--icon bw-item-close" onClick={() => setOpen(false)} aria-label={`Close ${entry.label}`}><X size={18} aria-hidden="true" /></button>}
        {onPutBack && !open && (
          <button type="button" className="bw-btn bw-btn--quiet" onClick={() => onPutBack(entry)}>{entry.kind === "media" ? "Use it" : "Put back"}</button>
        )}
      </div>
      {open && (
        <form className="bw-item-edit" onSubmit={(event) => void submit(event)}>
          {editable && Object.entries(values).map(([field, value]) => {
            const id = `bw-field-${entry.id}-${field}`;
            return (
              <label key={field} htmlFor={id}>
                <span>{FIELDS[field] ?? field}</span>
                {SHORT.has(field)
                  ? <input id={id} value={value} placeholder={hasExtras && field === "url" ? "https://…" : undefined} onChange={(event) => setValues({ ...values, [field]: event.target.value })} />
                  : <textarea id={id} rows={3} value={value} onChange={(event) => setValues({ ...values, [field]: event.target.value })} />}
              </label>
            );
          })}
          {hasExtras && <ItemPhoto photos={photos} value={image} saved={entry.image} onChange={setImage} />}
          {badLink && <p className="bw-item-note" role="alert">Links need to start with https://, mailto: or tel:.</p>}
          {failed && <p className="bw-item-note" role="alert">That didn’t save. Check it (links need to start with https://) and try again.</p>}
          {!entry.deletable && <p className="bw-item-note">It’s placed on your site. To take it off, ask Stax; it stays here either way.</p>}
          <div className="bw-actions">
            {editable && (
              <button type="submit" className="bw-btn bw-btn--primary" disabled={saving || blank || badLink || Object.keys(changed).length === 0}>
                {saving && <Spinner />}
                {saving ? "Saving…" : "Save"}
              </button>
            )}
            <button type="button" className="bw-btn bw-btn--quiet" onClick={() => setOpen(false)}>Cancel</button>
            {entry.deletable && (
              <button type="button" className="bw-btn bw-btn--text" disabled={saving} onClick={() => void library.remove(entry.id)}>Delete</button>
            )}
          </div>
        </form>
      )}
    </li>
  );
}

export function Deleted({ item, library }: { item: ContentLibrary["deleted"][number]; library: LibraryController }) {
  const saving = library.change?.recordId === item.id && library.change.status === "saving";
  const failed = library.change?.recordId === item.id && library.change.status === "failed";
  return (
    <li className="bw-item">
      <div className="bw-item-row">
        {item.src && <img className="bw-thumb" src={item.src} alt="" loading="lazy" />}
        <div className="bw-item-head">
          <span className="bw-item-label">{item.label}</span>
          <span className="bw-item-meta">{failed ? "Couldn’t restore it. Try again." : `${KINDS[item.kind]} · deleted`}</span>
        </div>
        <button type="button" className="bw-btn bw-btn--quiet" disabled={saving} onClick={() => void library.restore(item.id, item.fromDraftId)}>
          {saving && <Spinner />}
          {saving ? "Restoring…" : "Restore"}
        </button>
      </div>
    </li>
  );
}

/** List-row shapes, matching the content rows this replaces once the library loads. */
function ContentSkeleton() {
  return (
    <SkeletonGroup label="Loading your content" className="bw-items">
      {[80, 55, 65].map((width, index) => (
        <div key={index} className="bw-item">
          <div className="bw-item-row">
            <div className="bw-item-head">
              <Skeleton width={`${width}%`} height={15} radius={4} />
              <Skeleton width={`${Math.max(30, width - 25)}%`} height={12} radius={4} />
            </div>
          </div>
        </div>
      ))}
    </SkeletonGroup>
  );
}

interface ContentViewProps {
  library: LibraryController;
  /** Asks the admin to place something on the site again. */
  onPutBack: (entry: LibraryEntry) => void;
  /** The owner chose photos to add. */
  onPickPhotos: (files: File[]) => void;
  /** The photo form, while photos chosen here are being added. */
  photoAdd?: ReactNode;
  /** Which part of the library to show: text content, or photos. */
  show: "text" | "photos";
}

/** Everything the owner has given their admin, grouped by whether visitors can see it. */
export function ContentView({ library, onPutBack, onPickPhotos, photoAdd, show }: ContentViewProps) {
  const { library: data } = library;
  const picker = useRef<HTMLInputElement>(null);
  if (!data) {
    return library.failed ? (
      <div className="bw-unavailable" role="status">
        <strong>Your content couldn’t be loaded</strong>
        <button type="button" className="bw-btn bw-btn--quiet" onClick={library.retry}>Try again</button>
      </div>
    ) : <ContentSkeleton />;
  }
  const photos = data.entries.filter((entry) => entry.kind === "media");
  const text = data.entries.filter((entry) => entry.kind !== "media");
  const onSite = text.filter((entry) => entry.usedOn.length > 0 || entry.shared);
  const offSite = text.filter((entry) => entry.usedOn.length === 0 && !entry.shared);
  const deleted = data.deleted.filter((item) => (item.kind === "media") === (show === "photos"));
  const deletedSection = deleted.length > 0 && (
    <section aria-labelledby="bw-lib-deleted">
      <h3 id="bw-lib-deleted" className="bw-library-title">Deleted · can be restored</h3>
      <ul className="bw-items">{deleted.map((item) => <Deleted key={item.id} item={item} library={library} />)}</ul>
    </section>
  );
  if (show === "photos") {
    return (
      <div className="bw-library">
        <p className="bw-library-intro">Your photos. Stax places them on your pages; tap one to change its description.</p>
        <section aria-label="Photos">
          {photoAdd ?? (
            <>
              {photos.length > 0 && <ul className="bw-items">{photos.map((entry) => <Item key={entry.id} entry={entry} library={library} onPutBack={entry.usedOn.length === 0 && !entry.shared ? onPutBack : undefined} />)}</ul>}
              <input
                ref={picker}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/svg+xml,image/heic,image/heif"
                multiple
                hidden
                onChange={(event) => {
                  const files = [...(event.target.files ?? [])];
                  event.target.value = "";
                  if (files.length) onPickPhotos(files);
                }}
              />
              <button type="button" className="bw-btn bw-btn--quiet bw-add-photos" onClick={() => picker.current?.click()}>
                <ImagePlus size={18} aria-hidden="true" /> Add photos
              </button>
            </>
          )}
        </section>
        {deletedSection}
      </div>
    );
  }
  // Each list's full member order, by collection id, so Move up/down can submit an exact permutation.
  const listOrders = new Map<string, string[]>();
  for (const entry of text) {
    if (entry.list && !listOrders.has(entry.list.collectionId)) {
      listOrders.set(entry.list.collectionId, Array(entry.list.count).fill(""));
    }
    if (entry.list) listOrders.get(entry.list.collectionId)![entry.list.index] = entry.id;
  }
  return (
    <div className="bw-library">
      <p className="bw-library-intro">Everything you’ve given Stax. A new design never deletes any of it.</p>
      {offSite.length > 0 && (
        <section aria-labelledby="bw-lib-off">
          <h3 id="bw-lib-off" className="bw-library-title">Not on your site</h3>
          <ul className="bw-items">{offSite.map((entry) => <Item key={entry.id} entry={entry} library={library} onPutBack={onPutBack} listOrder={entry.list ? listOrders.get(entry.list.collectionId) : undefined} />)}</ul>
        </section>
      )}
      <section aria-labelledby="bw-lib-on">
        <h3 id="bw-lib-on" className="bw-library-title">On your site</h3>
        {onSite.length === 0
          ? <p className="bw-activity-empty">Nothing yet. Tell Stax about your business to get started.</p>
          : <ul className="bw-items">{onSite.map((entry) => <Item key={entry.id} entry={entry} library={library} listOrder={entry.list ? listOrders.get(entry.list.collectionId) : undefined} />)}</ul>}
      </section>
      {deletedSection}
    </div>
  );
}
